/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { Text, View } from 'react-native';
import { styles } from '../ReportsScreen.styles';
import { KpiStrip } from '../../../shared/surface-cards/KpiStrip';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { countActivePersonalSignals } from '../../domain/evaluatePersonalDashboard'
import type { PersonalDashboardDTO } from '../../domain/reportsPersonalTypes';
import { ReportsMetricTile } from './ReportsMetricTile';
import { formatMoney } from '../../../shared/format/public-api';

type Props = {
  dashboard: PersonalDashboardDTO | null;
  layoutWide?: boolean;
  previousSalesTotal?: number;
  previousSalesCount?: number;
};

function safeDeltaPct(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / previous;
}

function formatDelta(pct: number | null): string {
  if (pct == null) return '';
  const sign = pct >= 0 ? '\u2191' : '\u2193';
  return ` \u00B7 ${sign} ${Math.abs(pct * 100).toFixed(1)}% vs anterior`;
}

export function ReportsPersonalSummaryIndicators({ dashboard, layoutWide, previousSalesTotal, previousSalesCount }: Props) {
  if (!dashboard) {
    return (
      <View style={styles.personalFullWidthBlock}>
        <ReportsDenseListCard>
          <Text style={styles.reportsDenseBlockTitle}>Resumen del período</Text>
          <Text style={styles.caption}>No hay datos de personal. Conectate y recargá para ver el equipo.</Text>
        </ReportsDenseListCard>
      </View>
    );
  }

  const branch = dashboard.facts.branch;
  const signalCount = countActivePersonalSignals(dashboard);
  const operatorCount = dashboard.activeOperatorCount;
  const hasSignals = signalCount > 0;
  const voidedCount = branch.voidedSalesCount ?? 0;
  const showVoidedTile = voidedCount > 0;
  const tileLayout = layoutWide ? 'personalWide' : 'personal';

  return (
    <View style={styles.personalFullWidthBlock}>
      <KpiStrip title="Resumen del período">
        <View style={[styles.personalKpiGrid, layoutWide && styles.personalKpiGridWide]}>
          <ReportsMetricTile
            layout={tileLayout}
            label="Ventas del equipo"
            value={`${formatMoney(branch.salesTotal)}`}
            sub={`${branch.salesCount} ventas activas${formatDelta(safeDeltaPct(branch.salesTotal, previousSalesTotal ?? 0))}`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Operadores"
            value={String(operatorCount)}
            sub="con ventas en este filtro"
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Señales"
            value={String(signalCount)}
            sub="a revisar"
            warning={hasSignals}
            valueTone={hasSignals ? 'warning' : 'default'}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Ticket promedio"
            value={formatMoney(Math.round(branch.ticketAverage))}
            sub={`equipo${formatDelta(safeDeltaPct(branch.ticketAverage, (previousSalesCount ?? 0) > 0 ? (previousSalesTotal ?? 0) / (previousSalesCount ?? 1) : 0))}`}
          />
          {showVoidedTile ? (
            <ReportsMetricTile
              layout={tileLayout}
              label="Anulaciones"
              value={String(voidedCount)}
              sub={`${formatMoney((branch.voidedSalesTotal ?? 0))} anulado`}
              warning
              valueTone="warning"
            />
          ) : (
            <ReportsMetricTile
              layout={tileLayout}
              label="Productividad"
              value={operatorCount > 0 ? `${formatMoney(Math.round(branch.salesTotal / operatorCount))}` : '—'}
              sub={`${operatorCount} operadores en este filtro`}
            />
          )}
        </View>
      </KpiStrip>
    </View>
  );
}
